import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Star, Quote, UserCircle2, PlayCircle } from "lucide-react";

export default function Reviews() {
  const { t } = useLang();
  const [reviews, setReviews] = useState([]);
  useEffect(() => { api.get("/reviews").then(r => setReviews(r.data)).catch(()=>{}); }, []);

  return (
    <section id="reviews" className="section-pad bg-ink-800/30" data-testid="reviews">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="text-xs font-bold uppercase tracking-[0.22em] text-ice-400">Reviews</div>
        <h2 className="mt-3 font-display text-3xl md:text-5xl font-black">{t("reviews.title")}</h2>
        <p className="mt-3 text-silver-300 max-w-2xl">{t("reviews.sub")}</p>

        {reviews.length === 0 ? (
          <div className="mt-10 grid md:grid-cols-3 gap-5" data-testid="reviews-empty">
            {[0,1,2].map(i => (
              <div key={i} className="card-ink rounded-2xl p-6 border-dashed border-2 border-white/10 bg-transparent" data-testid={`review-placeholder-${i}`}>
                <Quote className="size-5 text-ice-400/60"/>
                <div className="mt-3 flex gap-0.5 text-silver-500">{Array.from({length:5}).map((_,k)=><Star key={k} className="size-4"/>)}</div>
                <p className="mt-3 text-silver-500 text-sm italic">Review will appear here once the owner publishes the first customer review from the admin dashboard.</p>
                <div className="mt-5 flex items-center gap-3">
                  <UserCircle2 className="size-10 text-silver-600"/>
                  <div>
                    <div className="text-sm font-semibold text-silver-400">Customer Name</div>
                    <div className="text-xs text-silver-500">City • Date</div>
                  </div>
                </div>
              </div>
            ))}
            <p className="md:col-span-3 mt-2 text-xs text-silver-500">No fake reviews. Real customer feedback will appear here as the owner publishes them.</p>
          </div>
        ) : (
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
            {reviews.map((r, i) => (
              <article key={r.id||i} className="card-ink rounded-2xl p-6 flex flex-col" data-testid={`review-${i}`}>
                <div className="flex gap-0.5 text-ice-400">{Array.from({length: r.rating || 5}).map((_,k)=><Star key={k} className="size-4 fill-current"/>)}</div>
                {r.text ? (
                  <p className="mt-3 text-silver-300 text-sm leading-relaxed line-clamp-[10]">&ldquo;{r.text}&rdquo;</p>
                ) : (
                  <p className="mt-3 text-silver-500 text-sm italic">Awaiting customer review content.</p>
                )}
                {r.photo && (
                  <div className="mt-4 rounded-xl overflow-hidden ring-1 ring-white/10 aspect-[16/10]">
                    <img src={r.photo} alt={`${r.name} — service`} className="w-full h-full object-cover"/>
                  </div>
                )}
                {r.video_url && (
                  <a href={r.video_url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-2 text-xs text-ice-400 hover:underline" data-testid={`review-video-${i}`}>
                    <PlayCircle className="size-4"/> Watch Video Testimonial
                  </a>
                )}
                <div className="mt-5 flex items-center gap-3 pt-4 border-t border-white/5">
                  <UserCircle2 className="size-10 text-silver-500"/>
                  <div>
                    <div className="text-sm font-semibold">{r.name}</div>
                    <div className="text-xs text-silver-400">
                      {r.city || ""}{r.city && r.date ? " • " : ""}{r.date || ""}
                    </div>
                  </div>
                </div>
              </article>
            ))}
            {/* Editable placeholders for future reviews */}
            {Array.from({length: Math.max(0, 3 - reviews.length)}).map((_, i) => (
              <div key={`ph-${i}`} className="card-ink rounded-2xl p-6 border-dashed border-2 border-white/10 bg-transparent" data-testid={`review-placeholder-future-${i}`}>
                <Quote className="size-5 text-ice-400/60"/>
                <div className="mt-3 flex gap-0.5 text-silver-500">{Array.from({length:5}).map((_,k)=><Star key={k} className="size-4"/>)}</div>
                <p className="mt-3 text-silver-500 text-sm italic">Future customer review — the owner can publish it from the admin dashboard.</p>
                <div className="mt-5 flex items-center gap-3">
                  <UserCircle2 className="size-10 text-silver-600"/>
                  <div>
                    <div className="text-sm font-semibold text-silver-400">Customer Name</div>
                    <div className="text-xs text-silver-500">City • Date</div>
                  </div>
                </div>
              </div>
            ))}
            <p className="md:col-span-3 mt-2 text-xs text-silver-500">More customer reviews will appear here as the owner adds them. Video testimonials are supported.</p>
          </div>
        )}
      </div>
    </section>
  );
}
